import { useCallback, useEffect, useState } from 'react';
import { Bell, Pause, Play, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentCard } from '../components/AgentDock';
import { PageLoader } from '../components/PageLoader';
import type { Watch } from '../lib/types';

const day = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

// The person's own watches: what Navaris keeps an eye on and comes back to them about.
export default function WatchesPage() {
    const { authFetch } = useAuth();
    const { openChat } = useAgentCard();
    const [watches, setWatches] = useState<Watch[] | null>(null);

    const reload = useCallback(() => {
        authFetch('/watches')
            .then(r => r.ok ? r.json() : [])
            .then(setWatches)
            .catch(() => setWatches([]));
    }, [authFetch]);
    useEffect(reload, [reload]);

    const change = async (w: Watch, init: RequestInit) => {
        const res = await authFetch(`/watches/${w.id}`, init).catch(() => null);
        if (res?.ok) reload();
    };
    const setStatus = (w: Watch, status: Watch['status']) => change(w, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    });
    const remove = (w: Watch) => {
        if (window.confirm(`Stop and remove this watch?\n\n${w.title}`)) change(w, { method: 'DELETE' });
    };

    if (watches === null) return <PageLoader label="Loading your watches..." />;

    return (
        <div data-agent-view>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold">Watches</h3>
                <p className="text-sm text-coolslate mt-1">
                    What Navaris keeps an eye on for you. When a watch fires, Navaris replies in the
                    conversation where you set it and sends you a notification. Ask Navaris in any
                    conversation to set a new one.
                </p>
            </div>

            {watches.length === 0 && (
                <p className="text-sm text-coolslate">
                    You have no watches. Navaris offers one when a conversation turns up something worth
                    following up, such as a figure falling below a level or a date coming up.
                </p>
            )}

            <div className="space-y-3">
                {watches.map(w => {
                    const paused = w.status === 'paused';
                    const next = day(w.next_run_at);
                    const last = day(w.last_fired_at);
                    return (
                        <div key={w.id} className={`bg-white border border-softgrey rounded-xl p-4 flex items-start gap-3 ${paused ? 'opacity-70' : ''}`}>
                            <Bell className="w-4 h-4 mt-0.5 shrink-0 text-brand-dark" aria-hidden />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-graphite">{w.title}</p>
                                <p className="text-[12px] text-coolslate mt-1">
                                    {[
                                        paused ? 'Paused' : w.checked,
                                        !paused && next ? `Next on ${next}` : null,
                                    ].filter(Boolean).join(' · ')}
                                    {last && (
                                        <>
                                            {(paused || w.checked || next) && ' · '}
                                            Last fired{' '}
                                            <button type="button" onClick={() => openChat(w.chat_id)} className="underline hover:text-brand-dark">
                                                {last}
                                            </button>
                                        </>
                                    )}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setStatus(w, paused ? 'active' : 'paused')}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-coolslate hover:text-brand-dark hover:bg-offwhite"
                                >
                                    {paused ? <Play className="w-3.5 h-3.5" aria-hidden /> : <Pause className="w-3.5 h-3.5" aria-hidden />}
                                    {paused ? 'Resume' : 'Pause'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(w)}
                                    aria-label="Remove"
                                    title="Remove"
                                    className="w-7 h-7 grid place-items-center rounded-md text-coolslate hover:text-red-600 hover:bg-offwhite"
                                >
                                    <Trash2 className="w-3.5 h-3.5" aria-hidden />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
